import { format } from 'date-fns';
import { generatePath } from 'react-router';
import { AUTO_ASSIGN_PROFILE } from '@/constants/routes';

export const profilePath = (deliveryProfileId) =>
    generatePath(AUTO_ASSIGN_PROFILE, { delivery_profile_id: deliveryProfileId });

// Every attached shipper as a filter option, sorted by name. A shipper has one assignment row per
// order type, so rows are deduped by shipper.
export const shipperOptions = (assignments = []) => {
    const names = new Map();
    for (const { shipper_id, shipper } of assignments) {
        if (shipper_id && !names.has(shipper_id)) names.set(shipper_id, shipper?.business_name || 'Unknown shipper');
    }
    return Array.from(names, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
};

// Distinct shipper ids per profile, in name order.
export const shippersByProfile = (assignments = []) => {
    const rank = new Map(shipperOptions(assignments).map(({ value }, i) => [value, i]));
    const shippers = {};
    for (const { delivery_profile_id, shipper_id } of assignments) {
        if (!delivery_profile_id || !shipper_id) continue;
        (shippers[delivery_profile_id] ??= new Set()).add(shipper_id);
    }
    return Object.fromEntries(
        Object.entries(shippers).map(([id, set]) => [id, [...set].sort((a, b) => rank.get(a) - rank.get(b))])
    );
};

// One grid row per profile. A profile without a subregion aggregate counts as 0 subregions.
export const profileRows = (profiles = [], assignments = []) => {
    const shippers = shippersByProfile(assignments);
    return profiles.map((profile) => ({
        delivery_profile_id: profile.delivery_profile_id,
        name: profile.name || '',
        commodity: profile.commodity || [],
        delivery_types: profile.delivery_types || [],
        subregion_count: profile.subregions_aggregate?.aggregate?.count ?? 0,
        shippers: shippers[profile.delivery_profile_id] || [],
        last_modified_at: profile.last_modified_at || null,
    }));
};

export const filterProfiles = (rows = [], search = '') => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => row.name.toLowerCase().includes(query));
};

// filterFn for a column whose value is an array: a row passes when it has any selected value.
export const includesAny = (row, columnId, selected) => {
    if (!selected?.length) return true;
    return (row.getValue(columnId) || []).some((value) => selected.includes(value));
};

export const formatModified = (timestamp) => (timestamp ? format(new Date(timestamp), 'MMM d, yyyy') : '-');
